
/* ============================== src/utils/tags.js ============================== */
// src/utils/tags.js
//
// Единственная точка правды о метках (тегах): правила нормализации,
// парсинга, валидации и форматирования.
//
// Никакого состояния и DOM - чистые функции. Формы, сервисы и
// компоненты не переопределяют эти правила локально: длина метки,
// количество, набор допустимых символов - константы этого модуля.
// Изменение правила = правка одного файла.
//
// Метка вводится только в поле «Метки» карточки - одной строкой,
// с разделителями-запятой или пробелом. Распознавания #tag в тексте
// описания нет: функционал удалён.

// ─── Константы ────────────────────────────────────────────────────────────

export const TAG_MAX_LENGTH = 32;
export const TAG_MAX_PER_ENTITY = 10;

export const TAG_CHIP_ARIA_TEMPLATE = (tag) => `Метка: ${tag}`;

// Допустимые символы метки: любые буквы, цифры, `_`, `-`, `.`.
export const TAG_ALLOWED_RE = /^[\p{L}\p{N}_\-.]+$/u;

// ─── Нормализация ─────────────────────────────────────────────────────────

/**
 * Единая нормализация одной метки: trim, отсечение ведущего #, lowercase.
 * Возвращает '' для пустого входа.
 */
export function normalizeTag(raw) {
  if (raw === null || raw === undefined) return "";
  let s = String(raw).trim();
  while (s.startsWith("#")) s = s.slice(1);
  return s.toLowerCase();
}

function isTagValid(norm) {
  if (!norm) return false;
  if (norm.length > TAG_MAX_LENGTH) return false;
  if (!TAG_ALLOWED_RE.test(norm)) return false;
  return true;
}

// ─── Парсинг источников ──────────────────────────────────────────────────

/**
 * Разбор строки поля «Метки». Разделители - `,` и пробелы.
 * Дедупликация, нормализация, ограничение по количеству (мягкое -
 * молчаливое отсечение).
 *
 * Возвращает { valid: string[], invalid: string[] }.
 * `invalid` содержит исходные токены (не нормализованные) - чтобы
 * в сообщении формы пользователь узнал то, что писал.
 */
export function parseTagsInput(raw) {
  if (raw === null || raw === undefined) return { valid: [], invalid: [] };
  const str = String(raw);
  const tokens = str.split(/[,\s]+/).filter(Boolean);
  const seen = new Set();
  const valid = [];
  const invalid = [];
  for (const tok of tokens) {
    const norm = normalizeTag(tok);
    if (!norm) continue;
    if (!isTagValid(norm)) {
      invalid.push(tok);
      continue;
    }
    if (seen.has(norm)) continue;
    seen.add(norm);
    valid.push(norm);
    if (valid.length >= TAG_MAX_PER_ENTITY) break;
  }
  return { valid, invalid };
}

/**
 * Разбор массива меток (старые данные, миграция, шаблоны).
 * Каждый элемент массива может сам содержать запятые и пробелы -
 * прогоняем через ту же логику.
 */
export function parseTagsFromArray(arr) {
  if (!Array.isArray(arr)) return [];
  const seen = new Set();
  const result = [];
  for (const item of arr) {
    if (item === null || item === undefined) continue;
    const parts = String(item)
      .split(/[,\s]+/)
      .filter(Boolean);
    for (const p of parts) {
      const norm = normalizeTag(p);
      if (!isTagValid(norm)) continue;
      if (seen.has(norm)) continue;
      seen.add(norm);
      result.push(norm);
      if (result.length >= TAG_MAX_PER_ENTITY) return result;
    }
  }
  return result;
}

// ─── Совместимость с templateSchemas ──────────────────────────────────────

/**
 * Санитайзер для templateSchemas: массив → нормализованный массив
 * либо null (пусто). null в sanitizeBySchema означает «поле удалено
 * из payload», это желаемое поведение.
 */
export function sanitizeTagsArray(value) {
  if (!Array.isArray(value)) return null;
  const arr = parseTagsFromArray(value);
  return arr.length > 0 ? arr : null;
}

// ─── История и аудит ─────────────────────────────────────────────────────

/** Формат для historyDelta / auditDelta: единая строка «a, b, c» или «-». */
export function formatTagsForHistory(tags) {
  if (!Array.isArray(tags) || tags.length === 0) return "-";
  return tags.join(", ");
}

/** Дешёвое сравнение массивов меток (порядок значим - как ввёл пользователь). */
export function isSameTags(a, b) {
  const aa = Array.isArray(a) ? a : [];
  const bb = Array.isArray(b) ? b : [];
  if (aa.length !== bb.length) return false;
  for (let i = 0; i < aa.length; i++) if (aa[i] !== bb[i]) return false;
  return true;
}
